import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  HistogramSeries,
  UTCTimestamp,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";

interface TradingViewChartProps {
  symbol?: string;
  companyName?: string;
  data?: any[];
  height?: number;
  timeRange?: string;
  previousClose?: number | null;
  chartType?: "line" | "candle";
}

const TradingViewChart = ({
  symbol = "RELIANCE",
  companyName = "Reliance Industries Limited",
  data = [],
  height = 400,
  timeRange = "1D",
  previousClose,
  chartType = "line",
}: TradingViewChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [legend, setLegend] = useState<any>(null);

  useEffect(() => {
    if (!chartContainerRef.current) {
      return;
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: "#ffffff" },
        textColor: "#333",
      },
      grid: {
        vertLines: { color: "#eeeeee" },
        horzLines: { color: "#eeeeee" },
      },
      crosshair: { mode: 1 },
      rightPriceScale: {
        borderColor: "#cccccc",
      },
      timeScale: {
        borderColor: "#cccccc",
        timeVisible: true,
        secondsVisible: false,
        rightBarStaysOnScroll: true,
      },
    });

    // 🔥 Universal Time Converter
    const toUtc = (time: any): UTCTimestamp => {
      // If already seconds (10 digits)
      if (typeof time === "number" && time < 9999999999) {
        return time as UTCTimestamp;
      }

      // If milliseconds
      if (typeof time === "number" && time > 9999999999) {
        return Math.floor(time / 1000) as UTCTimestamp;
      }

      // If string date
      return Math.floor(new Date(time).getTime() / 1000) as UTCTimestamp;
    };

    // 🔥 Prepare & Clean OHLC Data
    const ohlcData = data
      .map((d: any) => ({
        time: toUtc(d.time),
        open: Number(d.open),
        high: Number(d.high),
        low: Number(d.low),
        close: Number(d.close),
        value: Number(d.close),
      }))
      .filter(
        d =>
          !isNaN(d.time) &&
          !isNaN(d.open) &&
          !isNaN(d.high) &&
          !isNaN(d.low) &&
          !isNaN(d.close)
      )
      .sort((a, b) => a.time - b.time) // ✅ MUST SORT
      .filter(
        (item, index, self) => index === 0 || item.time !== self[index - 1].time // ✅ REMOVE DUPLICATES
      );

    // Add Series
    const mainSeries =
      chartType === "candle"
        ? chart.addSeries(CandlestickSeries, {
            upColor: "#089981",
            downColor: "#F23645",
            borderVisible: false,
            wickUpColor: "#089981",
            wickDownColor: "#F23645",
          })
        : chart.addSeries(AreaSeries, {
            lineColor: "#00D09C",
            topColor: "rgba(0,208,156,0.3)",
            bottomColor: "rgba(0,208,156,0)",
            lineWidth: 2,
          });

    mainSeries.setData(ohlcData as any);

    // 🔥 Volume Series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });

    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    const volumeData = data
      .map((d: any) => ({
        time: toUtc(d.time),
        value: Number(d.volume),
        color:
          Number(d.close) >= Number(d.open)
            ? "rgba(38,166,154,0.5)"
            : "rgba(239,83,80,0.5)",
      }))
      .sort((a, b) => a.time - b.time)
      .filter(
        (item, index, self) => index === 0 || item.time !== self[index - 1].time
      );

    volumeSeries.setData(volumeData as any);

    // Previous Close Line
    if (previousClose && timeRange === "1D") {
      mainSeries.createPriceLine({
        price: previousClose,
        color: "#999999",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: "Prev close",
      });
    }

    // Legend
    chart.subscribeCrosshairMove(param => {
      const price = param.seriesData.get(mainSeries) as any;
      if (!price) {
        return;
      }

      const open = price.open ?? price.value;
      const close = price.close ?? price.value;
      const high = price.high ?? price.value;
      const low = price.low ?? price.value;

      const change = close - open;
      const percent = open !== 0 ? (change / open) * 100 : 0;

      setLegend({
        open: open.toFixed(2),
        high: high.toFixed(2),
        low: low.toFixed(2),
        close: close.toFixed(2),
        change: change.toFixed(2),
        percent: percent.toFixed(2),
        color: close >= open ? "text-[#089981]" : "text-[#F23645]",
      });
    });

    chart.timeScale().fitContent();

    return () => {
      chart.remove();
    };
  }, [data, height, timeRange, previousClose, chartType]);

  return (
    <div className="group relative w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {/* Header Overlay */}
      <div className="pointer-events-none absolute left-0 top-0 z-10 flex w-full flex-col gap-1 p-3 text-slate-900">
        {/* Row 1: Symbol Info */}
        <div className="flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold uppercase text-white">
            {symbol.slice(0, 1)}
          </div>
          <span className="text-sm font-bold">{companyName}</span>
          <span className="rounded bg-gray-100 px-1 text-[10px] font-bold text-gray-600">
            1D
          </span>
          <span className="text-[10px] font-medium uppercase text-gray-400">
            NSE
          </span>
        </div>
        {/* Row 2: OHLC Legend */}
        {legend && (
          <div className="flex items-center gap-3 font-mono text-[11px]">
            <span className="font-medium text-gray-500">
              O <span className="text-gray-900">{legend.open}</span>
            </span>
            <span className="font-medium text-gray-500">
              H <span className="text-gray-900">{legend.high}</span>
            </span>
            <span className="font-medium text-gray-500">
              L <span className="text-gray-900">{legend.low}</span>
            </span>
            <span className="font-medium text-gray-500">
              C <span className="text-gray-900">{legend.close}</span>
            </span>
            <span className={`${legend.color} font-bold`}>
              {Number(legend.change) >= 0 ? "+" : ""}
              {legend.change} ({Number(legend.percent) >= 0 ? "+" : ""}
              {legend.percent}%)
            </span>
          </div>
        )}
      </div>

      <div ref={chartContainerRef} style={{ height: `${height}px` }} />
    </div>
  );
};

export default TradingViewChart;
